// =============================================================================
// C-Bridge — useCsvImport hook
// =============================================================================
//
// Thin wrapper rond de CSV-import endpoints voor de onboarding wizard +
// admin-pagina.
// =============================================================================

import { apiFetch } from '../../../core/shared/hooks/useApi.js';

export type EntityType = 'dossier' | 'lead' | 'contactpersoon';

export interface FieldDef {
  key: string;
  label: string;
  aliases: string[];
  required: boolean;
  type: 'string' | 'email' | 'phone' | 'date' | 'number' | 'enum';
  enumValues?: string[];
  example?: string;
}

export interface HeaderInfoResponse {
  headers: string[];
  delimiter: ',' | ';';
  sampleRows: Array<Record<string, string>>;
  totalRows: number;
  suggestedMappings: Record<string, string>;
  entityType: EntityType;
  fieldsCatalog: FieldDef[];
  fileName: string;
  fileSize: number;
}

export interface RowValidationError {
  rowIndex: number;
  field: string;
  message: string;
  rawValue?: string;
}

export interface ValidationResponse {
  validRowCount: number;
  errorRowCount: number;
  errors: RowValidationError[];
  rowStatus: Array<{ ok: boolean; errors: RowValidationError[] }>;
}

export interface ExecuteSyncResponse {
  async: false;
  created: number;
  skipped: number;
  errors: RowValidationError[];
}

export interface ExecuteAsyncResponse {
  async: true;
  jobId: string;
  total: number;
  validationErrors: RowValidationError[];
}

export interface JobStatusResponse {
  id: string;
  status: 'pending' | 'running' | 'done' | 'error';
  total: number;
  processed: number;
  result?: { created: number; skipped: number; errors: RowValidationError[] };
  errorMessage?: string;
}

// ─── API calls ───────────────────────────────────────────────────────────────

export async function uploadCsv(file: File, entityType: EntityType): Promise<HeaderInfoResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entityType', entityType);
  const token = localStorage.getItem('accessToken');
  const res = await fetch('/api/onboarding/csv/upload', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Upload mislukt (${res.status})`);
  }
  return res.json();
}

export function validateCsvRows(
  entityType: EntityType,
  mappings: Record<string, string>,
  rows: Array<Record<string, string>>,
): Promise<ValidationResponse> {
  return apiFetch<ValidationResponse>('/api/onboarding/csv/validate', {
    method: 'POST',
    body: JSON.stringify({ entityType, mappings, rows }),
  });
}

export function executeCsvImport(
  entityType: EntityType,
  mappings: Record<string, string>,
  rows: Array<Record<string, string>>,
): Promise<ExecuteSyncResponse | ExecuteAsyncResponse> {
  return apiFetch('/api/onboarding/csv/execute', {
    method: 'POST',
    body: JSON.stringify({ entityType, mappings, rows }),
  });
}

export function getImportJobStatus(jobId: string): Promise<JobStatusResponse> {
  return apiFetch<JobStatusResponse>(`/api/onboarding/csv/job/${jobId}`);
}

export function templateDownloadUrl(entityType: EntityType): string {
  return `/api/onboarding/csv/template/${entityType}`;
}
